import type { ReactElement } from "react";
import {
  Outlet,
  RouterProvider,
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
} from "@tanstack/react-router";

import { ActionPlansPage } from "@/pages/ActionPlansPage";
import { AdminPage, AuditLogPage } from "@/pages/AdminPage";
import { AssessmentWizardPage } from "@/pages/AssessmentWizardPage";
import { AssessmentsPage } from "@/pages/AssessmentsPage";
import { AssetsPage } from "@/pages/AssetsPage";
import { CountermeasuresPage } from "@/pages/CountermeasuresPage";
import { DashboardPage } from "@/pages/DashboardPage";
import { IncidentsPage } from "@/pages/IncidentsPage";
import { ReportsPage } from "@/pages/ReportsPage";
import { ThreatsPage } from "@/pages/ThreatsPage";
import { LoginPage } from "@/pages/auth/LoginPage";
import { RegisterPage } from "@/pages/auth/RegisterPage";
import { useAuthStore } from "@/stores/auth";

function requireAuth(): void {
  const { accessToken, bootstrapped } = useAuthStore.getState();
  if (bootstrapped && !accessToken) {
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({ to: "/login" });
  }
}

const rootRoute = createRootRoute({
  component: () => <Outlet />,
});

const dashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: requireAuth,
  component: DashboardPage,
});

const assetsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/assets",
  beforeLoad: requireAuth,
  component: AssetsPage,
});

const assessmentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/assessments",
  beforeLoad: requireAuth,
  component: AssessmentsPage,
});

const assessmentWizardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/assessments/$assessmentId/wizard",
  beforeLoad: requireAuth,
  component: AssessmentWizardPage,
});

const threatsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/threats",
  beforeLoad: requireAuth,
  component: ThreatsPage,
});

const countermeasuresRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/countermeasures",
  beforeLoad: requireAuth,
  component: CountermeasuresPage,
});

const reportsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reports",
  beforeLoad: requireAuth,
  component: ReportsPage,
});

const incidentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/incidents",
  beforeLoad: requireAuth,
  component: IncidentsPage,
});

const actionPlansRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/action-plans",
  beforeLoad: requireAuth,
  component: ActionPlansPage,
});

const adminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin",
  beforeLoad: requireAuth,
  component: AdminPage,
});

const auditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/audit",
  beforeLoad: requireAuth,
  component: AuditLogPage,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginPage,
});

const registerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/register",
  component: RegisterPage,
});

const routeTree = rootRoute.addChildren([
  dashboardRoute,
  assetsRoute,
  assessmentsRoute,
  assessmentWizardRoute,
  threatsRoute,
  countermeasuresRoute,
  reportsRoute,
  incidentsRoute,
  actionPlansRoute,
  adminRoute,
  auditRoute,
  loginRoute,
  registerRoute,
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

export function AppRouter(): ReactElement {
  return <RouterProvider router={router} />;
}
